import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Archive, ArchiveRestore, Edit2, Search, Printer, FileText, ShieldCheck, Ban, Fuel, Ship, Lock } from 'lucide-react';
import { PageShell, primaryButton, ghostButton, inputStyle, glassLight, T, Badge, StatCard, EmptyState, Field, SectionTitle, usePagination, Pagination, useIsMobile, CampoValidado, useConfirmacion, useDesplazarAlAbrir } from '../ui';
import { COMPARTIMIENTOS, PRODUCTOS_WOP, SUPLIDORES, TIPOS_DISPONIBLES, esWOP, etiquetaTipo, fmtFecha, fmtGal, imprimirPedido, normalizarTipo, nuevoToken, siguienteNumeroOrden } from './pedidoDocumento';
import type { Camion, Cola, Conductor, Envasadora, Pedido, PedidoLinea, PedidoTipo, ProductoWOP } from '../types';

// Ícono con el que se reconoce cada suplidor de un vistazo. Coastal despacha
// por terminal marítima, de ahí el barco.
const ICONO_TIPO: Record<PedidoTipo, React.ElementType> = {
  GLP_REFINERIA: Fuel,
  GLP_COASTAL: Ship,
  WOP: Fuel
};
// Color del distintivo en el listado, para distinguir a qué suplidor fue cada
// orden sin tener que leer la etiqueta.
const COLOR_TIPO: Record<PedidoTipo, {
  color: string;
  soft: string;
}> = {
  GLP_REFINERIA: {
    color: T.brand,
    soft: T.accentSoft
  },
  GLP_COASTAL: {
    color: T.info,
    soft: T.infoSoft
  },
  WOP: {
    color: T.textMuted,
    soft: T.surfaceSubtle
  }
};

// ── EL NÚMERO DE ORDEN NO SE TOCA ───────────────────────────────────────────
// Lo asigna el sistema siguiendo la secuencia de cada suplidor (Refinería y
// Coastal llevan la suya). Un número tecleado a mano se repite o se salta, y
// este va impreso en un documento que sale de la empresa.
//
// Va como campo de solo lectura y no como texto suelto a propósito: con texto
// suelto, quien intenta borrarlo no consigue nada y no entiende por qué. Así
// el cursor entra, la tecla no escribe, y el aviso dice el motivo.
function NumeroOrdenFijo({
  numero,
  editando
}: {
  numero: string;
  editando: boolean;
}) {
  const isMobile = useIsMobile();

  // Se guarda también el número de intento, y no solo el texto: como el aviso
  // dice siempre lo mismo, sin ese contador React no vería ningún cambio de
  // estado, el temporizador no se reiniciaría, y el aviso se apagaría a los
  // 3,2 segundos del PRIMER intento aunque siguieras dándole a la tecla.
  const [aviso, setAviso] = useState<{
    texto: string;
    intento: number;
  } | null>(null);

  // Se borra solo: es una corrección momentánea, no un error pendiente de
  // resolver.
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), 3200);
    return () => clearTimeout(t);
  }, [aviso]);
  const negar = () => setAviso(prev => ({
    texto: editando ? 'El número de orden no se puede cambiar: quedó fijado al crear la orden.' : 'El número de orden no se puede escribir ni borrar: lo asigna el sistema.',
    intento: (prev?.intento ?? 0) + 1
  }));

  // Escribir, borrar o pegar avisan. Copiar y moverse con las flechas no:
  // llevarse el número al portapapeles es legítimo.
  const intentaCambiar = (e: React.KeyboardEvent) => e.key === 'Backspace' || e.key === 'Delete' || e.key.length === 1 && !e.ctrlKey && !e.metaKey;
  const leyenda = editando ? 'no se cambia' : 'se asigna solo';
  return <div data-id="element-869" data-mp="3t863epl2pha2">
      <div style={{
      position: 'relative'
    }} data-id="element-870" data-mp="3t7nv2ul4zcls">
        <input readOnly value={numero || '—'} title="Lo asigna el sistema: no se puede cambiar" aria-label="Orden de pedido número. Lo asigna el sistema y no se puede cambiar." style={{
        ...inputStyle,
        background: aviso ? 'rgba(220,38,38,.05)' : T.surfaceSubtle,
        border: aviso ? '1px solid rgba(220,38,38,.55)' : inputStyle.border,
        color: T.text,
        fontWeight: 800,
        fontSize: 16,
        letterSpacing: '.02em',
        paddingRight: isMobile ? 104 : 128,
        cursor: 'default'
      }} onKeyDown={e => {
        if (intentaCambiar(e)) {
          e.preventDefault();
          negar();
        }
      }} onPaste={e => {
        e.preventDefault();
        negar();
      }} onCut={e => {
        e.preventDefault();
        negar();
      }} onDrop={e => {
        e.preventDefault();
        negar();
      }} data-id="element-871" data-mp="c0u5xxlz1lyv" />

        <span style={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        color: aviso ? T.danger : T.textMuted,
        letterSpacing: 0,
        whiteSpace: 'nowrap',
        pointerEvents: 'none'
      }} data-id="element-872" data-mp="1ip9g51d0kqps">

          <Lock size={11} data-id="element-873" data-mp="zyic8fbspsis" />
          {leyenda}
        </span>
      </div>

      {aviso ? <div style={{
      marginTop: 5,
      fontSize: 11.5,
      fontWeight: 600,
      color: T.danger,
      display: 'flex',
      alignItems: 'center',
      gap: 5
    }} data-id="element-874" data-mp="14wtlanu9nyzy">

          <span style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: T.dangerSoft,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        flexShrink: 0
      }} data-id="element-875" data-mp="2g9dvhtfzotyh">

            !
          </span>
          {aviso.texto}
        </div> : null}
    </div>;
}

// Fecha en yyyy-mm-dd tomada del calendario LOCAL. `toISOString()` convierte a
// UTC, y como República Dominicana va cuatro horas atrás, después de las 8 de
// la noche devolvía el día siguiente: las órdenes de la tarde quedaban
// fechadas mañana.
const isoLocal = (d: Date) => {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};
const hoyISO = () => isoLocal(new Date());

// Atajos del filtro de fecha. La semana arranca el lunes.
type RangoFecha = 'todas' | 'hoy' | 'semana' | 'mes';
const RANGOS: {
  id: RangoFecha;
  label: string;
}[] = [{
  id: 'todas',
  label: 'Todas'
}, {
  id: 'hoy',
  label: 'Hoy'
}, {
  id: 'semana',
  label: 'Esta semana'
}, {
  id: 'mes',
  label: 'Este mes'
}];
const limitesRango = (modo: RangoFecha): {
  desde: string;
  hasta: string;
} | null => {
  if (modo === 'todas') return null;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  if (modo === 'hoy') {
    return {
      desde: isoLocal(hoy),
      hasta: isoLocal(hoy)
    };
  }
  if (modo === 'semana') {
    const desdeLunes = (hoy.getDay() + 6) % 7; // domingo (0) cuenta como 6
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - desdeLunes);
    const domingo = new Date(lunes);
    domingo.setDate(lunes.getDate() + 6);
    return {
      desde: isoLocal(lunes),
      hasta: isoLocal(domingo)
    };
  }
  const primero = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const ultimo = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  return {
    desde: isoLocal(primero),
    hasta: isoLocal(ultimo)
  };
};
const totalGalones = (p: Pedido) => p.lineas.reduce((sum, l) => sum + (l.galones || 0), 0);
const estadoStyle = (estado: Pedido['estado']) => {
  if (estado === 'autorizado') return {
    color: T.ok,
    soft: T.okSoft,
    label: 'Autorizado'
  };
  if (estado === 'anulado') return {
    color: T.danger,
    soft: T.dangerSoft,
    label: 'Anulado'
  };
  return {
    color: T.warn,
    soft: T.warnSoft,
    label: 'Borrador'
  };
};
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};

// Línea vacía para un compartimiento que todavía no se llenó.
const lineaVacia = (compartimiento: number): PedidoLinea => ({
  compartimiento,
  producto: 'gasoilRegular',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const lineaGLPVacia = (): PedidoLinea => ({
  compartimiento: 1,
  producto: 'glp',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const pedidoVacio = (): Partial<Pedido> => ({
  tipo: 'GLP_REFINERIA',
  numeroOrden: '',
  fecha: hoyISO(),
  conductor: '',
  paseChofer: '',
  fichaCola: '',
  placaCamion: '',
  lineas: [lineaGLPVacia()],
  estado: 'borrador',
  observaciones: ''
});
interface PedidosProps {
  pedidos: Pedido[];
  onAdd: (p: Pedido) => void;
  onUpdate: (p: Pedido) => void;
  onArchivar: (pedido: Pedido, archivar: boolean) => void;
  // Catálogos de "Flota y Destinos". Alimentan los desplegables del
  // formulario: elegir un conductor trae su pase, su placa, su cola y sus
  // galones; elegir una envasadora trae su RNC. Se mantienen en su propio
  // módulo, aquí solo se consultan.
  conductores: Conductor[];
  envasadoras: Envasadora[];
  camiones: Camion[];
  colas: Cola[];
  // Si el usuario no tiene permiso de ver Flota, no tiene sentido mandarlo
  // allá cuando las listas están vacías.
  puedeVerFlota: boolean;
  // Permisos resueltos por el rol del usuario (ver App.tsx).
  canEdit: boolean;
  canArchive: boolean;
  canAutorizar: boolean;
  // Quién firma cuando se autoriza una orden.
  usuarioNombre: string;
  usuarioCargo: string;
  onLog?: (mensaje: string) => void;
}
export function Pedidos({
  pedidos,
  onAdd,
  onUpdate,
  onArchivar,
  // Los catálogos llevan valor por defecto a propósito: si por lo que sea
  // llegan sin definir (App.tsx sin actualizar, caché a medias), el módulo
  // sigue en pie y se comporta como si no hubiera nada registrado, en vez de
  // dejar la pantalla en blanco.
  conductores = [],
  envasadoras = [],
  camiones = [],
  colas = [],
  puedeVerFlota = false,
  canEdit,
  canArchive,
  canAutorizar,
  usuarioNombre,
  usuarioCargo,
  onLog
}: PedidosProps) {
  const isMobile = useIsMobile();
  const [dialogo, confirmar] = useConfirmacion();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<'todos' | PedidoTipo>('todos');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'borrador' | 'autorizado' | 'anulado'>('todos');
  const [filterFecha, setFilterFecha] = useState<RangoFecha>('todas');
  const [verArchivados, setVerArchivados] = useState(false);
  const [form, setForm] = useState<Partial<Pedido>>(pedidoVacio());
  const lineas = form.lineas ?? [];
  // Igual que en Flota: editar una orden del final del listado abre el
  // formulario arriba, así que hay que llevar la vista hasta él.
  const refForm = useDesplazarAlAbrir<HTMLFormElement>(showForm, editingId);

  // ── FILTRADO ──────────────────────────────────────────────────────────
  // El listado muestra las órdenes en curso o las archivadas, nunca mezcladas.
  // Todo lo demás (buscador, filtros, contadores) trabaja sobre este conjunto.
  const enVista = useMemo(() => pedidos.filter(p => verArchivados ? !!p.archivado : !p.archivado), [pedidos, verArchivados]);
  const totalArchivadas = useMemo(() => pedidos.filter(p => !!p.archivado).length, [pedidos]);

  // Las fechas son yyyy-mm-dd, así que comparar con < y > alfabéticamente da
  // el mismo resultado que comparar como fechas y evita construir Dates.
  const filtrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const rango = limitesRango(filterFecha);
    return enVista.filter(p => filterTipo === 'todos' || normalizarTipo(p.tipo) === filterTipo).filter(p => filterEstado === 'todos' || p.estado === filterEstado).filter(p => !rango || !!p.fecha && p.fecha >= rango.desde && p.fecha <= rango.hasta).filter(p => {
      if (!q) return true;
      const enDestinos = p.lineas.some(l => l.destino.toLowerCase().includes(q) || l.rncDestino.toLowerCase().includes(q));
      return p.numeroOrden.toLowerCase().includes(q) || p.conductor.toLowerCase().includes(q) || p.placaCamion.toLowerCase().includes(q) || enDestinos;
    }).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [enVista, searchTerm, filterTipo, filterEstado, filterFecha]);

  // Cuántas órdenes caen en cada atajo de fecha: saberlo antes de hacer clic
  // ahorra el viaje de ir y volver cuando el rango está vacío.
  const conteoRango = useMemo(() => {
    const cuenta = {} as Record<RangoFecha, number>;
    for (const r of RANGOS) {
      const lim = limitesRango(r.id);
      cuenta[r.id] = lim ? enVista.filter(p => !!p.fecha && p.fecha >= lim.desde && p.fecha <= lim.hasta).length : enVista.length;
    }
    return cuenta;
  }, [enVista]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtrados);

  // Al devolver la última orden archivada no tiene sentido quedarse mirando una
  // sección vacía: se vuelve solo al listado, que es justo donde acaba de
  // reaparecer la orden.
  useEffect(() => {
    if (verArchivados && totalArchivadas === 0) {
      setVerArchivados(false);
      setPage(1);
    }
  }, [verArchivados, totalArchivadas]);

  // ── ESTADÍSTICAS ──────────────────────────────────────────────────────
  // Los recuadros de arriba cuentan solo las órdenes en curso: una archivada
  // ya no está en el listado, así que sumarla haría que los números no
  // cuadraran con lo que se ve debajo.
  const stats = useMemo(() => {
    const activas = pedidos.filter(p => !p.archivado);
    const vivos = activas.filter(p => p.estado !== 'anulado');
    const mes = hoyISO().slice(0, 7);
    return {
      total: activas.length,
      borradores: activas.filter(p => p.estado === 'borrador').length,
      autorizados: activas.filter(p => p.estado === 'autorizado').length,
      galonesMes: vivos.filter(p => (p.fecha || '').startsWith(mes)).reduce((s, p) => s + totalGalones(p), 0)
    };
  }, [pedidos]);

  // WOP ya no se ofrece al crear, pero si quedan órdenes viejas del formato
  // hace falta poder filtrarlas. El filtro aparece solo si existen.
  const hayWOP = useMemo(() => pedidos.some(p => esWOP(p.tipo)), [pedidos]);

  // ── FORMULARIO ────────────────────────────────────────────────────────
  const abrirNuevo = () => {
    const vacio = pedidoVacio();
    setForm({
      ...vacio,
      numeroOrden: siguienteNumeroOrden(pedidos, normalizarTipo(vacio.tipo ?? ''))
    });
    setEditingId(null);
    setShowForm(true);
  };
  const abrirEdicion = (p: Pedido) => {
    setForm({
      ...p,
      lineas: p.lineas.map(l => ({
        ...l
      }))
    });
    setEditingId(p.id);
    setShowForm(true);
  };
  const cerrarForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(pedidoVacio());
  };

  // Cambiar de suplidor no toca el detalle: los dos formatos de GLP son el
  // mismo papel. Solo hay que rehacer las líneas si se viene de una orden
  // vieja de WOP, porque sus 6 compartimientos con producto no son
  // intercambiables con la línea única de GLP.
  const cambiarTipo = (tipo: PedidoTipo) => {
    setForm(prev => {
      const veniaDeWOP = esWOP(prev.tipo ?? '');
      const vaAWOP = esWOP(tipo);
      // Cada suplidor lleva su propia secuencia, así que al cambiar de uno a
      // otro el número tiene que cambiar también. Solo en órdenes nuevas y si
      // nadie lo ha escrito a mano.
      // Una orden ya creada conserva su número: es el que el suplidor tiene
      // registrado y el que va impreso en el papel.
      const numeroOrden = editingId ? prev.numeroOrden : siguienteNumeroOrden(pedidos, tipo);
      if (veniaDeWOP === vaAWOP) return {
        ...prev,
        tipo,
        numeroOrden
      };
      return {
        ...prev,
        tipo,
        numeroOrden,
        lineas: vaAWOP ? COMPARTIMIENTOS.map(lineaVacia) : [lineaGLPVacia()]
      };
    });
  };
  // ── CATÁLOGOS Y REGLAS ────────────────────────────────────────────────
  const conductoresActivos = useMemo(() => (conductores ?? []).filter(c => c.activo !== false).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')), [conductores]);
  const envasadorasActivas = useMemo(() => (envasadoras ?? []).filter(e => e.activo !== false).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')), [envasadoras]);
  const camionesActivos = useMemo(() => (camiones ?? []).filter(c => c.activo !== false), [camiones]);
  const colasActivas = useMemo(() => (colas ?? []).filter(c => c.activo !== false), [colas]);

  // El chofer de una orden vieja puede no estar registrado (o haberse dado de
  // baja). Se le agrega su propia opción para que el desplegable no lo pierda
  // al editar.
  const conductorFuera = !!form.conductor?.trim() && !conductoresActivos.some(c => c.nombre === form.conductor);
  const conductorActual = useMemo(() => conductoresActivos.find(c => c.nombre === form.conductor), [conductoresActivos, form.conductor]);
  // El destino manda: de él dependen qué colas y qué camiones se pueden usar.
  const destinoActual = useMemo(() => envasadorasActivas.find(e => e.nombre === (form.lineas?.[0]?.destino ?? '')), [envasadorasActivas, form.lineas]);

  // Un conductor sin unidades asignadas puede llevar cualquiera de las que
  // estén en servicio; con unidades asignadas, solo las suyas.
  const camionesDelConductor = useMemo(() => {
    const suyosLista = conductorActual?.camiones ?? [];
    if (!suyosLista.length) return camionesActivos;
    const suyos = new Set(suyosLista.map(k => k.id));
    return camionesActivos.filter(c => suyos.has(c.id));
  }, [conductorActual, camionesActivos]);
  const colasDelConductor = useMemo(() => {
    const suyasLista = conductorActual?.colas ?? [];
    if (!suyasLista.length) return colasActivas;
    const suyas = new Set(suyasLista.map(k => k.id));
    return colasActivas.filter(c => suyas.has(c.id));
  }, [conductorActual, colasActivas]);

  // Camiones que pueden hacer ESTE viaje: ni vetados en la planta de destino,
  // ni reservados a otro chofer.
  const camionesValidos = useMemo(() => camionesDelConductor.filter(c => (!destinoActual || !(c.plantasVetadas ?? []).includes(destinoActual.id)) && (!c.soloConductorId || c.soloConductorId === conductorActual?.id)), [camionesDelConductor, destinoActual, conductorActual]);

  // Colas admitidas por la planta, separadas en primera y segunda opción.
  // Sin destino elegido todavía, se ofrecen todas las del conductor.
  const {
    colasPrimera,
    colasSegunda
  } = useMemo(() => {
    if (!destinoActual) return {
      colasPrimera: colasDelConductor,
      colasSegunda: [] as typeof colasDelConductor
    };
    const segundas = destinoActual.colasSegundaOpcion ?? [];
    const permitidas = destinoActual.colasPermitidas ?? [];
    return {
      colasPrimera: colasDelConductor.filter(c => permitidas.length ? permitidas.includes(c.id) : !segundas.includes(c.id)),
      colasSegunda: colasDelConductor.filter(c => segundas.includes(c.id))
    };
  }, [destinoActual, colasDelConductor]);
  const colaPorFicha = (ficha: string) => colasActivas.find(c => c.ficha === ficha);
  const camionPorPlaca = (placa: string) => camionesActivos.find(c => c.placa === placa);

  // Qué tiene de malo la combinación elegida. Se calcula en cada render para
  // poder avisar mientras se arma la orden, no solo al intentar guardarla.
  const problemas: string[] = [];
  if (destinoActual) {
    const camion = camionPorPlaca(form.placaCamion ?? '');
    if (camion && (camion.plantasVetadas ?? []).includes(destinoActual.id)) {
      problemas.push(`${camion.nombre} (${camion.placa}) no puede entrar a ${destinoActual.nombre}.`);
    }
    if (camion?.soloConductorId && camion.soloConductorId !== conductorActual?.id) {
      problemas.push(`${camion.nombre} solo lo puede llevar ${camion.soloConductorNombre}.`);
    }
    const cola = colaPorFicha(form.fichaCola ?? '');
    const permitidas = destinoActual.colasPermitidas ?? [];
    const segundas = destinoActual.colasSegundaOpcion ?? [];
    if (cola && permitidas.length && !permitidas.includes(cola.id) && !segundas.includes(cola.id)) {
      problemas.push(`${destinoActual.nombre} no admite la cola ${cola.ficha}.`);
    }
  }

  // Elegir el conductor arrastra su unidad: pase, placa y ficha de la cola.
  // Es el punto de todo el registro de flota: media orden se llena de un solo
  // clic, y sin teclear no hay erratas en un papel que sale de la empresa.
  const elegirConductor = (nombre: string) => {
    const c = conductoresActivos.find(x => x.nombre === nombre);
    setForm(prev => ({
      ...prev,
      conductor: nombre,
      paseChofer: c?.pase || prev.paseChofer,
      placaCamion: c?.camiones?.[0]?.placa || prev.placaCamion,
      fichaCola: c?.colas?.[0]?.ficha || prev.fichaCola
    }));
  };

  // Elegir la cola decide los galones del viaje: son suyos, no del chofer ni
  // del camión. La misma cola carga siempre lo mismo.
  const elegirCola = (ficha: string) => {
    const cola = colaPorFicha(ficha);
    setForm(prev => ({
      ...prev,
      fichaCola: ficha,
      lineas: cola?.capacidadGalones ? (prev.lineas ?? []).map((l, i) => i === 0 ? {
        ...l,
        galones: cola.capacidadGalones
      } : l) : prev.lineas
    }));
  };

  // Elegir el destino trae su RNC, que es el otro dato que se imprime.
  const elegirEnvasadora = (index: number, nombre: string) => {
    const e = envasadorasActivas.find(x => x.nombre === nombre);
    setLinea(index, {
      destino: nombre,
      rncDestino: e?.rnc ?? ''
    });
  };
  const setLinea = (index: number, patch: Partial<PedidoLinea>) => {
    setForm(prev => ({
      ...prev,
      lineas: (prev.lineas ?? []).map((l, i) => i === index ? {
        ...l,
        ...patch
      } : l)
    }));
  };
  // Qué falta para poder guardar. Se calcula en cada render para poder
  // señalar el campo concreto en vez de soltar un alert con una sola queja
  // a la vez.
  const faltantes: Record<string, string> = {};
  if (!form.fecha) faltantes.fecha = 'Indica la fecha del pedido';
  if (!form.conductor?.trim()) faltantes.conductor = 'Indica el conductor';
  if (!form.placaCamion?.trim()) faltantes.placaCamion = 'Indica la placa del camión';
  const lineasUtiles = (form.lineas ?? []).filter(l => (l.galones || 0) > 0);
  if (lineasUtiles.length === 0) {
    faltantes.lineas = esWOP(form.tipo ?? '') ? 'Registra al menos un compartimiento con galones' : 'Indica la cantidad de galones';
  } else if (lineasUtiles.some(l => !l.destino.trim())) {
    faltantes.lineas = 'Cada línea con galones necesita su destino';
  }
  const [mostrarFaltantes, setMostrarFaltantes] = useState(false);
  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    // Las reglas de flota se comprueban aquí también, no solo en pantalla: el
    // formulario se puede haber armado en otro orden y el aviso quedar fuera
    // de vista. Un documento con una combinación imposible no debe salir.
    if (problemas.length) {
      setMostrarFaltantes(true);
      return;
    }
    // Los errores solo se destapan al intentar guardar: marcar campos en rojo
    // apenas se abre el formulario es hostil.
    if (Object.keys(faltantes).length) {
      setMostrarFaltantes(true);
      return;
    }
    // El número se vuelve a calcular justo antes de guardar, no se usa el que
    // se puso al abrir el formulario. Con el tiempo real, otro usuario puede
    // haber creado una orden mientras este formulario estaba abierto: ese
    // número ya estaría ocupado y la base lo rechazaría por el índice único.
    // Como el campo ya no se puede corregir a mano, esto tiene que salir bien
    // solo.
    const base = {
      ...form,
      numeroOrden: editingId ? form.numeroOrden : siguienteNumeroOrden(pedidos, normalizarTipo(form.tipo ?? '')),
      lineas: lineasUtiles
    } as Pedido;
    const galones = lineasUtiles.reduce((sum, l) => sum + (l.galones || 0), 0);
    const ok = await confirmar({
      titulo: editingId ? '¿Guardar los cambios?' : '¿Crear esta orden de pedido?',
      mensaje: editingId ? 'La orden se actualizará con los datos del formulario.' : 'La orden quedará en borrador hasta que alguien la autorice.',
      detalles: [{
        etiqueta: 'Orden',
        valor: base.numeroOrden
      }, {
        etiqueta: 'Formato',
        valor: etiquetaTipo(base.tipo)
      }, {
        etiqueta: 'Conductor',
        valor: base.conductor
      }, {
        etiqueta: 'Placa',
        valor: base.placaCamion
      }, {
        etiqueta: lineasUtiles.length === 1 ? 'Destino' : 'Destinos',
        valor: String(lineasUtiles.length)
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(galones)} galones`
      }],
      confirmar: editingId ? 'Guardar cambios' : 'Crear orden',
      tono: 'accent'
    });
    if (!ok) return;
    if (editingId) {
      onUpdate({
        ...base,
        id: editingId
      });
      onLog?.(`Orden de pedido editada: ${base.numeroOrden} (${etiquetaTipo(base.tipo)})`);
    } else {
      onAdd({
        ...base,
        id: Date.now().toString(),
        estado: 'borrador',
        autorizadoPor: '',
        autorizadoCargo: '',
        autorizadoFecha: '',
        // El token se acuña al autorizar, no al crear: un borrador todavía
        // no es un documento verificable.
        token: '',
        fecha_creacion: hoyISO()
      });
      onLog?.(`Orden de pedido creada: ${base.numeroOrden} (${etiquetaTipo(base.tipo)})`);
    }
    setMostrarFaltantes(false);
    cerrarForm();
  };

  // ── AUTORIZACIÓN ──────────────────────────────────────────────────────
  // Autorizar equivale a firmar el formulario: se estampa quién lo hizo y
  // desde ahí el pedido ya no se edita.
  const autorizar = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Autorizar esta orden de pedido?',
      mensaje: `Tu nombre quedará estampado en el "AUTORIZADO POR" del formulario y se generará su código QR de verificación.`,
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Formato',
        valor: etiquetaTipo(p.tipo)
      }, {
        etiqueta: 'Conductor',
        valor: p.conductor || '—'
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }, {
        etiqueta: 'Autoriza',
        valor: usuarioNombre
      }],
      advertencia: 'Una vez autorizada, la orden ya no se puede editar.',
      confirmar: 'Sí, autorizar',
      tono: 'ok'
    });
    if (!ok) return;
    onUpdate({
      ...p,
      estado: 'autorizado',
      autorizadoPor: usuarioNombre,
      autorizadoCargo: usuarioCargo,
      autorizadoFecha: hoyISO(),
      // Se acuña una sola vez: si la orden ya se autorizó antes (y se
      // reautoriza tras una corrección) el QR impreso sigue siendo válido.
      token: p.token || nuevoToken()
    });
    onLog?.(`Orden de pedido autorizada: ${p.numeroOrden} por ${usuarioNombre}`);
  };
  const anular = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Anular esta orden?',
      mensaje: 'La orden queda registrada pero marcada como anulada. El formulario impreso saldrá con la marca "ANULADO" y quien escanee su QR verá que ya no es válida.',
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }],
      confirmar: 'Sí, anular',
      tono: 'warn'
    });
    if (!ok) return;
    onUpdate({
      ...p,
      estado: 'anulado'
    });
    onLog?.(`Orden de pedido anulada: ${p.numeroOrden}`);
  };
  // Las órdenes no se borran. Archivar las saca del listado y nada más: la
  // orden sigue guardada, se puede volver a ver y el QR de una autorizada se
  // sigue verificando. Para invalidar una orden está Anular, que es otra cosa.
  const archivar = async (p: Pedido, archivando: boolean) => {
    const ok = await confirmar({
      titulo: archivando ? '¿Archivar esta orden?' : '¿Devolver esta orden al listado?',
      mensaje: archivando ? 'Sale del listado principal y queda en "Ver archivados". No se borra nada: la orden se conserva y se puede devolver cuando quieras.' : 'Vuelve al listado principal de órdenes.',
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Estado',
        valor: estadoStyle(p.estado).label
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }],
      confirmar: archivando ? 'Sí, archivar' : 'Sí, devolver',
      tono: archivando ? 'warn' : 'accent'
    });
    if (!ok) return;
    onArchivar(p, archivando);
    onLog?.(`Orden de pedido ${archivando ? 'archivada' : 'devuelta al listado'}: ${p.numeroOrden}`);
  };

  // ── IMPRESIÓN ─────────────────────────────────────────────────────────
  const imprimir = (p: Pedido) => {
    imprimirPedido(p);
    onLog?.(`Orden de pedido impresa: ${p.numeroOrden}`);
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return <><PageShell title="Órdenes de Pedido" sub="Formulario de orden de pedido / retiro bajo contrato" action={canEdit ? <button onClick={abrirNuevo} style={primaryButton} data-id="element-877" data-mp="j5xytkgc11ho">
            <Plus size={15} data-id="element-878" data-mp="f67cbsxu97iy" /> Nueva orden
          </button> : undefined} data-id="element-876" data-mp="9vldfv8kxraj">

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
        gap: 14,
        marginBottom: 26
      }} data-id="element-879" data-mp="sydf8dbzdi0g">

        <StatCard label="Órdenes" value={String(stats.total)} note="registradas" data-id="element-880" data-mp="96hvho9iygs" />
        <StatCard label="Borradores" value={String(stats.borradores)} note="sin autorizar" accent={T.warn} data-id="element-881" data-mp="b9ggq3hdzx4" />

        <StatCard label="Autorizadas" value={String(stats.autorizados)} note="listas para despacho" accent={T.ok} data-id="element-882" data-mp="a7mbbrarijtg" />

        <StatCard label="Galones del mes" value={fmtGal(stats.galonesMes)} note="AG solicitados" accent={T.info} data-id="element-883" data-mp="57zwjdfacvij" />

      </div>

      {/* ── FILTROS ── */}
      <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        marginBottom: 20
      }} data-id="element-884" data-mp="1lovu0bm2h7tp">

        <div style={{
          position: 'relative',
          flex: '1 1 220px'
        }} data-id="element-885" data-mp="35od4grbco2xl">

          <Search size={15} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-886" data-mp="30pa2xgukweud" />

          <input style={{
            ...inputStyle,
            paddingLeft: 34
          }} placeholder="Buscar por orden, conductor, placa o destino" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-887" data-mp="2rovck32rc9k0" />

        </div>
        <select style={{
          ...inputStyle,
          flex: '0 1 190px'
        }} value={filterTipo} onChange={e => setFilterTipo(e.target.value as typeof filterTipo)} data-id="element-888" data-mp="3kekgao6uqnpb">

          <option value="todos" data-id="element-889" data-mp="3ao9bz0j9achw">Todos los formatos</option>
          {TIPOS_DISPONIBLES.map(t => <option key={t} value={t} data-id="element-890" data-mp="bxzepsgjuww8">
              {SUPLIDORES[t].etiqueta}
            </option>)}
          {hayWOP && <option value="WOP" data-id="element-891" data-mp="76hcz26pcsa7">{SUPLIDORES.WOP.etiqueta}</option>}
        </select>
        <select style={{
          ...inputStyle,
          flex: '0 1 160px'
        }} value={filterEstado} onChange={e => setFilterEstado(e.target.value as typeof filterEstado)} data-id="element-892" data-mp="ei5ju3gwgted">

          <option value="todos" data-id="element-893" data-mp="og35qkbaee3t">Todos los estados</option>
          <option value="borrador" data-id="element-894" data-mp="oeiaesh5er4w">Borrador</option>
          <option value="autorizado" data-id="element-895" data-mp="shezn2171lht">Autorizado</option>
          <option value="anulado" data-id="element-896" data-mp="siztepfop0em">Anulado</option>
        </select>
        {/* Las órdenes archivadas no desaparecen: se ven desde aquí y se
         pueden devolver al listado. El botón aparece cuando hay alguna, pero
         también mientras se está viendo la sección: si solo dependiera del
         contador, al devolver la última se esfumaría y no quedaría forma de
         salir de una pantalla vacía. */}
        {(totalArchivadas > 0 || verArchivados) && <button type="button" onClick={() => {
          setVerArchivados(v => !v);
          setPage(1);
          cerrarForm();
        }} style={{
          padding: '9px 15px',
          borderRadius: 99,
          fontSize: 12.5,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          whiteSpace: 'nowrap',
          color: verArchivados ? T.warn : T.textSecondary,
          background: verArchivados ? T.warnSoft : 'transparent',
          border: `1px solid ${verArchivados ? `${T.warn}44` : T.border}`,
          transition: 'all .15s'
        }} data-id="element-897" data-mp="1g7rdetj4ecgb">

            <Archive size={14} data-id="element-898" data-mp="3vyn0dlh78ihe" />
            {verArchivados ? 'Viendo archivadas' : 'Ver archivadas'}
            <span style={{
            fontSize: 11,
            fontWeight: 800,
            padding: '1px 7px',
            borderRadius: 99,
            background: verArchivados ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
            color: verArchivados ? T.warn : T.textMuted
          }} data-id="element-899" data-mp="dta449ugbt9p">

              {totalArchivadas}
            </span>
          </button>}
      </div>

      {/* ── FILTRO POR FECHA ──
           Va en botones y no en otro desplegable porque son los cuatro cortes
           que se piden todos los días: un clic en vez de abrir y elegir. */}
      <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 20
      }} data-id="element-900" data-mp="v3x6rt4bfvjg">

        {RANGOS.map(r => {
          const activo = filterFecha === r.id;
          return <button key={r.id} type="button" onClick={() => setFilterFecha(r.id)} style={{
            padding: '9px 15px',
            borderRadius: 99,
            fontSize: 12.5,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            whiteSpace: 'nowrap',
            color: activo ? T.accent : T.textSecondary,
            background: activo ? T.accentSoft : 'transparent',
            border: `1px solid ${activo ? T.accentBorder : T.border}`,
            transition: 'all .15s'
          }} data-id="element-901" data-mp="7ci05o5ias2e">

              {r.label}
              <span style={{
              fontSize: 11,
              fontWeight: 800,
              padding: '1px 7px',
              borderRadius: 99,
              background: activo ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
              color: activo ? T.accent : T.textMuted
            }} data-id="element-902" data-mp="180a6ujkiio1u">

                {conteoRango[r.id]}
              </span>
            </button>;
        })}
      </div>

      {/* ── FORMULARIO ── */}
      <AnimatePresence data-id="element-903" data-mp="p3r020x6lc8g">
        {showForm && <motion.form ref={refForm} onSubmit={guardar} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} style={{
          ...glassLight,
          padding: isMobile ? 18 : 24,
          marginBottom: 24,
          overflow: 'hidden'
        }} data-id="element-904" data-mp="kaxvpe5mk5ww">

            <SectionTitle data-id="element-905" data-mp="1c3i48hwdbib">
              {editingId ? 'Editar orden de pedido' : 'Nueva orden de pedido'}
            </SectionTitle>

            <Field label="Suplidor" data-id="element-906" data-mp="2cz5lpj1vmucz">
              <div style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap'
            }} data-id="element-907" data-mp="2h1ae6egl9ky1">

                {/* WOP solo se ofrece si esta orden ya venía en ese formato:
                   las nuevas son siempre GLP, pero editar una vieja no debe
                   cambiarle el formato a la fuerza. */}
                {(esWOP(form.tipo ?? '') ? [...TIPOS_DISPONIBLES, 'WOP' as PedidoTipo] : TIPOS_DISPONIBLES).map(t => {
                const activo = normalizarTipo(form.tipo ?? '') === t;
                const Icono = ICONO_TIPO[t];
                return <button key={t} type="button" onClick={() => cambiarTipo(t)} style={{
                  ...(activo ? primaryButton : ghostButton),
                  flex: '1 1 180px',
                  justifyContent: 'center'
                }} data-id="element-908" data-mp="2382ncp0c0the">

                      <Icono size={15} data-id="element-909" data-mp="1fj9p2lw8m9jm" />
                      {SUPLIDORES[t].etiqueta}
                    </button>;
              })}
              </div>
              <div style={{
              marginTop: 8,
              fontSize: 11.5,
              color: T.textMuted
            }} data-id="element-910" data-mp="17h948ldrsq0u">

                El formulario sale dirigido a{' '}
                <strong style={{
                color: T.textSecondary
              }} data-id="element-911" data-mp="1hbj2uktb2kk0">

                  {SUPLIDORES[normalizarTipo(form.tipo ?? '')].razonSocial}
                </strong>
              </div>
            </Field>

            <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 16
          }} data-id="element-912" data-mp="kjfmvyg1s4we">

              <Field label="Orden de pedido número" data-id="element-913" data-mp="yk8iynlzz81m">
                <NumeroOrdenFijo numero={form.numeroOrden ?? ''} editando={!!editingId} data-id="element-914" data-mp="tjay239lrzwf" />
              </Field>
              <Field label="Fecha *" data-id="element-915" data-mp="29x161l3uf8bc">
                <input type="date" style={inputStyle} value={form.fecha ?? ''} onChange={e => setForm({
                ...form,
                fecha: e.target.value
              })} data-id="element-916" data-mp="2a06vx2kaqe84" />

              </Field>
              {/* El destino manda: de él dependen qué cola y qué camión se
                 pueden usar, así que se elige antes que nada. */}
              {!esWOP(form.tipo ?? '') && <div style={{
              gridColumn: isMobile ? 'auto' : '1 / -1'
            }} data-id="element-917" data-mp="3dxipxgzcwhaq">
                  <Field label="Destino (envasadora) *" data-id="element-918" data-mp="3ivkgemz43iw8">
                    <select style={inputStyle} value={form.lineas?.[0]?.destino ?? ''} onChange={e => elegirEnvasadora(0, e.target.value)} data-id="element-919" data-mp="aepg9f9p8clu">
                      <option value="" data-id="element-920" data-mp="eg171o4b73vm">
                        {envasadorasActivas.length ? '— Elige el destino —' : '— Sin envasadoras registradas —'}
                      </option>
                      {envasadorasActivas.map(env => <option key={env.id} value={env.nombre} data-id="element-921" data-mp="5mehifqp2wuv">
                          {env.nombre}{env.ciudad ? ` · ${env.ciudad}` : ''}
                        </option>)}
                      {!!form.lineas?.[0]?.destino && !envasadorasActivas.some(env => env.nombre === form.lineas![0].destino) && <option value={form.lineas[0].destino} data-id="element-922" data-mp="2u99aeoshzc7n">
                          {form.lineas[0].destino} (no registrada)
                        </option>}
                    </select>
                    {destinoActual ? <div style={{
                  marginTop: 6,
                  fontSize: 11.5,
                  color: T.textMuted
                }} data-id="element-923" data-mp="2kg29bg4siidd">
                        La cola y el camión de abajo ya salen filtrados por lo
                        que admite {destinoActual.nombre}.
                      </div> : null}
                  </Field>
                </div>}
              <Field label="Conductor del camión *" data-id="element-924" data-mp="337bmbzdrrp9p">
                <select style={inputStyle} value={form.conductor ?? ''} onChange={e => elegirConductor(e.target.value)} data-id="element-925" data-mp="2z7b403njutxd">
                  <option value="" data-id="element-926" data-mp="1rtpsw20vn1id">
                    {conductoresActivos.length ? '— Elige un conductor —' : '— Sin conductores registrados —'}
                  </option>
                  {conductoresActivos.map(c => <option key={c.id} value={c.nombre} data-id="element-927" data-mp="3av38f8x4vhv">
                      {c.nombre}{c.pase ? ` · ${c.pase}` : ''}
                    </option>)}
                  {/* Chofer de una orden vieja que ya no está en la lista */}
                  {conductorFuera && <option value={form.conductor} data-id="element-928" data-mp="c8oz5p2rtn4i">
                      {form.conductor} (no registrado)
                    </option>}
                </select>
                {!conductoresActivos.length && puedeVerFlota ? <div style={{
                marginTop: 6,
                fontSize: 11.5,
                color: T.textMuted
              }} data-id="element-929" data-mp="qzecoy3pfebj">

                    Regístralos en <strong style={{
                  color: T.textSecondary
                }} data-id="element-930" data-mp="v1j2tmuy1stf">Flota y Destinos</strong> y aparecerán aquí con su
                    placa, su cola y sus galones.
                  </div> : null}
              </Field>
              <Field label="Pase del chofer" data-id="element-931" data-mp="31r7gskfdu76n">
                <CampoValidado modo="codigo" valor={form.paseChofer ?? ''} onChange={v => setForm({
                ...form,
                paseChofer: v
              })} placeholder="APP-87" maxLength={15} data-id="element-932" data-mp="31rzdjykpuayi" />

              </Field>
              {/* La cola sale filtrada por lo que admite la planta. Las de
                 segunda opción van en su propio grupo: se pueden usar, pero
                 no son la primera elección. */}
              <Field label="Ficha de la cola" data-id="element-933" data-mp="3sc9lmes2dqfz">
                {colasActivas.length ? <select style={inputStyle} value={form.fichaCola ?? ''} onChange={e => elegirCola(e.target.value)} data-id="element-934" data-mp="2usoqxb64ipp0">
                    <option value="" data-id="element-935" data-mp="2znlx8ac0qky7">— Elige la cola —</option>
                    {colasPrimera.map(c => <option key={c.id} value={c.ficha} data-id="element-936" data-mp="34myiiqn8rjyo">
                        {c.ficha}{c.capacidadGalones ? ` · ${fmtGal(c.capacidadGalones)} gal` : ''}
                      </option>)}
                    {colasSegunda.length ? <optgroup label="Segunda opción" data-id="element-937" data-mp="2byjgr5t4ey5a">
                        {colasSegunda.map(c => <option key={c.id} value={c.ficha} data-id="element-938" data-mp="2gxm9prvttdww">
                            {c.ficha}{c.capacidadGalones ? ` · ${fmtGal(c.capacidadGalones)} gal` : ''}
                          </option>)}
                      </optgroup> : null}
                    {/* La cola ya elegida no desaparece aunque el destino la
                     deje fuera: se muestra marcada y el aviso explica. */}
                    {!!form.fichaCola && ![...colasPrimera, ...colasSegunda].some(c => c.ficha === form.fichaCola) && <option value={form.fichaCola} data-id="element-939" data-mp="f6rq0at4ujr4">
                        {form.fichaCola} (no admitida aquí)
                      </option>}
                  </select> : <CampoValidado modo="codigo" valor={form.fichaCola ?? ''} onChange={v => setForm({
                ...form,
                fichaCola: v
              })} placeholder="58-01" maxLength={15} data-id="element-940" data-mp="6cn5sa8i86kk" />}

              </Field>
              <Field label="Placa del camión *" data-id="element-941" data-mp="3sjv5avwqtj8o">
                {camionesActivos.length ? <select style={inputStyle} value={form.placaCamion ?? ''} onChange={e => setForm({
                ...form,
                placaCamion: e.target.value
              })} data-id="element-942" data-mp="3nl0fvb49swfl">
                    <option value="" data-id="element-943" data-mp="3ix0zf5dni9r1">— Elige el camión —</option>
                    {camionesValidos.map(c => <option key={c.id} value={c.placa} data-id="element-944" data-mp="34xhq4993ssd5">
                        {c.nombre} · {c.placa}
                      </option>)}
                    {!!form.placaCamion && !camionesValidos.some(c => c.placa === form.placaCamion) && <option value={form.placaCamion} data-id="element-945" data-mp="jt2us1b6bi7">
                        {form.placaCamion} (no permitido aquí)
                      </option>}
                  </select> : <CampoValidado modo="placa" valor={form.placaCamion ?? ''} onChange={v => setForm({
                ...form,
                placaCamion: v
              })} placeholder="L-259163" data-id="element-946" data-mp="3nvu180jkscbd" />}

              </Field>
            </div>


            <SectionTitle data-id="element-947" data-mp="jgu88dyrydmo">
              {esWOP(form.tipo ?? '') ? 'Detalle por compartimiento' : 'Detalle del despacho'}
            </SectionTitle>

            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginBottom: 18
          }} data-id="element-948" data-mp="28yrhhgl75eel">

              {lineas.map((linea, i) => <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : esWOP(form.tipo ?? '') ? '96px 1.3fr 100px 1.4fr 1fr' : '160px 1fr',
              gap: 10,
              alignItems: 'center',
              padding: '10px 12px',
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.rMd
            }} data-id="element-949" data-mp="23xlm1b05jd1d" data-mpi={i}>

                  {esWOP(form.tipo ?? '') && <span style={{
                fontSize: 12,
                fontWeight: 700,
                color: T.text
              }} data-id="element-950" data-mp="1zycqc0ljx536">

                      Compart. {linea.compartimiento}
                    </span>}
                  {esWOP(form.tipo ?? '') && <select style={inputStyle} value={linea.producto} onChange={e => setLinea(i, {
                producto: e.target.value as ProductoWOP
              })} data-id="element-951" data-mp="3bf2s97ixzaqz">

                      {PRODUCTOS_WOP.map(prod => <option key={prod.id} value={prod.id} data-id="element-952" data-mp="1kw4ktrrm5u4o">
                          {prod.label}
                        </option>)}
                    </select>}
                  {/* Los galones los pone la cola. Se dejan editables por si
                   un viaje sale con menos de lo habitual. */}
                  <Field label={esWOP(form.tipo ?? '') ? '' : 'Galones'} data-id="element-953" data-mp="1yvx6v9s73m6b">
                    <CampoValidado modo="numeros" valor={linea.galones ? String(linea.galones) : ''} onChange={v => setLinea(i, {
                  galones: Number(v) || 0
                })} placeholder="Galones" maxLength={7} data-id="element-954" data-mp="1ytt6v2xf37bb" />

                  </Field>

                  {/* En GLP el destino se elige arriba, una sola vez. En WOP
                   cada compartimiento puede ir a un sitio distinto. */}
                  {esWOP(form.tipo ?? '') && <select style={inputStyle} value={linea.destino} onChange={e => elegirEnvasadora(i, e.target.value)} data-id="element-955" data-mp="2ml8acz0xjuo5">
                    <option value="" data-id="element-956" data-mp="2wqgaw86gxp3g">
                      {envasadorasActivas.length ? '— Elige el destino —' : '— Sin envasadoras registradas —'}
                    </option>
                    {envasadorasActivas.map(env => <option key={env.id} value={env.nombre} data-id="element-957" data-mp="2ms0lqar04pwl">
                        {env.nombre}
                      </option>)}
                    {/* Destino escrito a mano en una orden anterior */}
                    {!!linea.destino && !envasadorasActivas.some(env => env.nombre === linea.destino) && <option value={linea.destino} data-id="element-958" data-mp="3ahuwmmrrg4e7">
                        {linea.destino} (no registrada)
                      </option>}
                  </select>}

                  <Field label={esWOP(form.tipo ?? '') ? '' : 'RNC del destino'} data-id="element-959" data-mp="31orjygoxvvkm">
                    <CampoValidado modo="rnc" valor={linea.rncDestino} onChange={v => setLinea(i, {
                  rncDestino: v
                })} placeholder="RNC del destino" data-id="element-960" data-mp="31moupyvm26gy" />

                  </Field>
                </div>)}
            </div>

            {esWOP(form.tipo ?? '') && <div style={{
            fontSize: 11,
            color: T.textMuted,
            marginBottom: 16,
            marginTop: -8
          }} data-id="element-961" data-mp="1rix7on8by8yd">

                Los compartimientos sin galones no se imprimen en el formulario.
              </div>}

            <Field label="Observaciones" data-id="element-962" data-mp="1mqncarog4gop">
              <input style={inputStyle} value={form.observaciones ?? ''} onChange={e => setForm({
              ...form,
              observaciones: e.target.value
            })} placeholder="Opcional" data-id="element-963" data-mp="1mpc8wsywxay4" />

            </Field>

            {/* Reglas de flota: se avisa mientras se arma la orden, sin
               esperar a que le den a guardar. */}
            {problemas.length ? <div style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 12,
            background: T.dangerSoft,
            border: `1px solid ${T.danger}33`,
            color: T.danger,
            fontSize: 12.5,
            lineHeight: 1.5
          }} data-id="element-964" data-mp="13tcg5fy423dh">

                <strong data-id="element-965" data-mp="mjryxp2svbc9">Esta combinación no se puede despachar:</strong>
                <ul style={{
              margin: '6px 0 0',
              paddingLeft: 18
            }} data-id="element-966" data-mp="hkx9i4abuoj6">
                  {problemas.map((p, i) => <li key={i} data-id="element-967" data-mp="wacv75v043s0" data-mpi={i}>{p}</li>)}
                </ul>
              </div> : null}

            {mostrarFaltantes && Object.keys(faltantes).length ? <div style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 12,
            background: T.dangerSoft,
            border: `1px solid ${T.danger}33`,
            color: T.danger,
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.6
          }} data-id="element-968" data-mp="159gf2c1zsn7k">
              Falta completar: {Object.values(faltantes).join(' · ')}
            </div> : null}
            <div style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end'
          }} data-id="element-969" data-mp="3h0c1zc0b86t1">

              <button type="button" onClick={cerrarForm} style={ghostButton} data-id="element-970" data-mp="3c20psumjlk7n">
                Cancelar
              </button>
              <button type="submit" style={primaryButton} data-id="element-971" data-mp="2v63nkkp55w9g">
                {editingId ? 'Guardar cambios' : 'Crear orden'}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>

      {/* ── LISTADO ── */}
      {filtrados.length === 0 ? <EmptyState title={verArchivados ? 'Sin órdenes archivadas' : 'Sin órdenes de pedido'} hint={verArchivados ? 'Las órdenes que archives aparecerán aquí y podrás devolverlas al listado.' : canEdit ? 'Crea la primera orden para imprimir el formulario de Refidomsa.' : 'Tu rol no permite crear órdenes de pedido.'} icon={<FileText size={22} data-id="element-973" data-mp="2h8f1xan92l3c" />} data-id="element-972" data-mp="34vdh84zmvmzo" /> : <>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }} data-id="element-974" data-mp="2m5yhcdvop3wv">

            {pageItems.map(p => {
            const es = estadoStyle(p.estado);
            // Una orden archivada no se edita ni se autoriza: primero hay que
            // devolverla al listado, así no se toca sin querer algo guardado.
            const bloqueado = p.estado !== 'borrador' || !!p.archivado;
            return <motion.div key={p.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} style={{
              ...glassLight,
              padding: isMobile ? 16 : '18px 20px',
              display: 'flex',
              gap: 16,
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap'
            }} data-id="element-975" data-mp="1gynsyqd8b35m">

                  <div style={{
                flex: '1 1 260px',
                minWidth: 0
              }} data-id="element-976" data-mp="t9coaxsrcom7">

                    <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginBottom: 6
                }} data-id="element-977" data-mp="obb58cvc0paq">

                      <span style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: T.text
                  }} data-id="element-978" data-mp="38xf0r2dbe40t">

                        Orden {p.numeroOrden || '—'}
                      </span>
                      <Badge label={etiquetaTipo(p.tipo)} color={COLOR_TIPO[normalizarTipo(p.tipo)].color} soft={COLOR_TIPO[normalizarTipo(p.tipo)].soft} dot={false} data-id="element-979" data-mp="3ilwvb1v03ew0" />

                      <Badge label={es.label} color={es.color} soft={es.soft} data-id="element-980" data-mp="2py11c4wtg12x" />
                    </div>
                    <div style={{
                  fontSize: 12,
                  color: T.textSecondary
                }} data-id="element-981" data-mp="2kzzgpfjqrtpc">

                      {fmtFecha(p.fecha)} · {p.conductor || 'Sin conductor'} ·{' '}
                      {p.placaCamion || 'Sin placa'}
                    </div>
                    <div style={{
                  fontSize: 12,
                  color: T.textMuted,
                  marginTop: 3
                }} data-id="element-982" data-mp="e8qe18zo6qm6">

                      {p.lineas.length}{' '}
                      {p.lineas.length === 1 ? 'destino' : 'destinos'} ·{' '}
                      {fmtGal(totalGalones(p))} galones
                      {p.estado === 'autorizado' && p.autorizadoPor ? ` · Autorizó ${p.autorizadoPor}` : ''}
                    </div>
                  </div>

                  <div style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap'
              }} data-id="element-983" data-mp="crkx1ogcuk0v">

                    <button type="button" onClick={() => imprimir(p)} style={iconBtn} title="Imprimir formulario" data-id="element-984" data-mp="10jrpwdmvg8vj">

                      <Printer size={14} data-id="element-985" data-mp="qrvootpho2n3" />
                    </button>

                    {canAutorizar && p.estado === 'borrador' && !p.archivado && <button type="button" onClick={() => autorizar(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.ok}55`,
                  color: T.ok
                }} title="Autorizar orden" data-id="element-986" data-mp="vpnhdpxyjssc">

                        <ShieldCheck size={14} data-id="element-987" data-mp="1o8azqxlkktpq" />
                      </button>}

                    {canEdit && !bloqueado && <button type="button" onClick={() => abrirEdicion(p)} style={iconBtn} title="Editar" data-id="element-988" data-mp="1y0vwythi3n1d">

                        <Edit2 size={14} data-id="element-989" data-mp="1t22kn4dya22s" />
                      </button>}

                    {canArchive && p.estado === 'autorizado' && <button type="button" onClick={() => anular(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.warn}55`,
                  color: T.warn
                }} title="Anular orden" data-id="element-990" data-mp="1k3h3ndo4mv44">

                        <Ban size={14} data-id="element-991" data-mp="1accoodygba9a" />
                      </button>}

                    {canArchive && <button type="button" onClick={() => archivar(p, !p.archivado)} style={{
                  ...iconBtn,
                  borderColor: p.archivado ? `${T.info}55` : `${T.warn}55`,
                  color: p.archivado ? T.info : T.warn
                }} title={p.archivado ? 'Devolver al listado' : 'Archivar orden'} data-id="element-992" data-mp="15djbkmn2tl9n">

                        {p.archivado ? <ArchiveRestore size={14} data-id="element-993" data-mp="rgmjkhv81xr6" /> : <Archive size={14} data-id="element-994" data-mp="33256w1tjp5k2" />}
                      </button>}
                  </div>

                </motion.div>;
          })}
          </div>

          <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="órdenes" data-id="element-995" data-mp="37z55xsrz981o" />

        </>}
    </PageShell>
    {dialogo}
  </>;
}